import { Download, Eye, FileText, XCircle } from "lucide-react";
import type { KycDocument, KycRecord } from "@/types/kyc";
import { matchDoc, OPTIONAL_DOC_LABELS, REQUIRED_DOCS, requiredDocCount, requiredUploadedCount } from "../data";

interface DocumentsCardProps {
  record: KycRecord;
  onPreview: (doc: KycDocument) => void;
  onDownload: (doc: KycDocument) => void;
}

function resolveDocUrl(url?: string): string {
  if (!url) return "";
  if (url.startsWith("data:") || url.startsWith("blob:") || url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `http://localhost:5001${url.startsWith("/") ? "" : "/"}${url}`;
}

function isPdf(doc?: KycDocument | null): boolean {
  if (!doc) return false;
  const fileName = (doc.fileName || (doc as any).name || "").toLowerCase();
  const url = (doc.previewUrl || (doc as any).url || "").toLowerCase();
  return fileName.endsWith(".pdf") || url.includes(".pdf") || url.startsWith("data:application/pdf");
}

export default function DocumentsCard({ record, onPreview, onDownload }: DocumentsCardProps) {
  const required = record.businessType ? REQUIRED_DOCS[record.businessType] || [] : [];
  const uploaded = requiredUploadedCount(record);
  const requiredTotal = requiredDocCount(record.businessType);

  const docs = Array.isArray(record.documents) ? record.documents : [];
  const optionalUploaded = docs.filter((d) => {
    const t = (d.docType || (d as any).type || "").toLowerCase();
    return OPTIONAL_DOC_LABELS.some((l) => l.toLowerCase() === t);
  });

  return (
    <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
      <h3 className="text-lg font-semibold text-ink">Documents</h3>
      <p className="mt-0.5 text-[13px] text-ink-secondary">
        {uploaded} of {requiredTotal} uploaded
      </p>

      <div className="mt-3 flex flex-col gap-2.5">
        {required.map((label) => {
          const doc = matchDoc(record, label);
          if (!doc) {
            return (
              <div
                key={label}
                className="flex h-16 items-center gap-3 rounded-lg border border-line bg-[#FEF2F2] px-3"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#FEF2F2] text-[#DC2626]">
                  <XCircle className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{label}</p>
                  <p className="text-[13px] text-[#DC2626]">Not uploaded</p>
                </div>
              </div>
            );
          }

          const docUrl = resolveDocUrl(doc.previewUrl || (doc as any).url);
          const isPdfFile = isPdf(doc);
          const fileName = doc.fileName || (doc as any).name || label;
          const fileSize = doc.fileSize || (doc as any).size || "";

          return (
            <div
              key={label}
              className="flex h-16 items-center gap-3 rounded-lg border border-line px-3"
            >
              {isPdfFile || !docUrl ? (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-rose-50 text-rose-500 border border-rose-100">
                  <FileText className="h-5 w-5" />
                </span>
              ) : (
                <img
                  src={docUrl}
                  alt={fileName}
                  className="h-10 w-10 shrink-0 rounded-md object-cover border border-line"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">{label}</p>
                <p className="truncate text-xs text-ink-secondary">
                  {fileName} {fileSize ? `· ${fileSize}` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onPreview(doc)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                aria-label={`View ${fileName}`}
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => onDownload(doc)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                aria-label={`Download ${fileName}`}
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          );
        })}

        {optionalUploaded.map((doc, idx) => {
          const docUrl = resolveDocUrl(doc.previewUrl || (doc as any).url);
          const isPdfFile = isPdf(doc);
          const docTitle = doc.docType || (doc as any).type || "Optional Document";
          const fileName = doc.fileName || (doc as any).name || "document";
          const fileSize = doc.fileSize || (doc as any).size || "";

          return (
            <div key={doc.docType || idx} className="flex h-16 items-center gap-3 rounded-lg border border-line px-3">
              {isPdfFile || !docUrl ? (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-500 border border-blue-100">
                  <FileText className="h-5 w-5" />
                </span>
              ) : (
                <img
                  src={docUrl}
                  alt={fileName}
                  className="h-10 w-10 shrink-0 rounded-md object-cover border border-line"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">{docTitle}</p>
                <p className="truncate text-xs text-ink-secondary">
                  {fileName} {fileSize ? `· ${fileSize}` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onPreview(doc)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                aria-label={`View ${fileName}`}
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => onDownload(doc)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                aria-label={`Download ${fileName}`}
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}